import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, RefreshCw, Sparkles, HandHeart, Heart, Loader2, Info, X } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CircleAvatar } from "@/components/CircleAvatar";
import { BlessPicker } from "@/components/BlessPicker";
import { QuickBless } from "@/components/QuickBless";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { userCoords } from "@/hooks/useGeo";
import { normRecipient, normFeed, money, RADIUS_OPTIONS, UNLIMITED_RADIUS, clockTime } from "@/lib/blessed";

// Pins sit in a band BELOW the CTA buttons and ABOVE the bottom bubble,
// well spaced so they don't overlap each other or the feed card.
const pinSlots = [
  { top: "54%", left: "20%" }, { top: "54%", left: "74%" },
  { top: "62%", left: "46%" }, { top: "70%", left: "26%" },
  { top: "70%", left: "70%" }, { top: "58%", left: "60%" },
  { top: "66%", left: "14%" }, { top: "66%", left: "86%" },
  { top: "74%", left: "44%" }, { top: "56%", left: "40%" },
  { top: "72%", left: "58%" }, { top: "60%", left: "88%" },
];

export default function MapPage() {
  const { user, refreshUser } = useAuth();
  const coords = useMemo(() => userCoords(user), [user]);
  const ready = !!coords;
  const navigate = useNavigate();
  const [radius, setRadius] = useState(() => {
    const s = localStorage.getItem("blessed_radius");
    return s !== null ? JSON.parse(s) : 100;
  });
  useEffect(() => { localStorage.setItem("blessed_radius", JSON.stringify(radius)); }, [radius]);
  const [people, setPeople] = useState([]);
  const [feed, setFeed] = useState([]);
  const [stats, setStats] = useState({ today: 0, all: 0 });
  const [selected, setSelected] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [feedIdx, setFeedIdx] = useState(0);
  const [zipOpen, setZipOpen] = useState(false);
  const [zip, setZip] = useState("");
  const [savingZip, setSavingZip] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  const saveZip = async () => {
    if (!/^\d{5}$/.test(zip)) { toast.error("Enter a valid 5-digit US zip code."); return; }
    setSavingZip(true);
    try {
      await api.post("/users/me/zipcode", { zipcode: zip });
      await refreshUser();
      toast.success("Location updated! 📍");
      setZipOpen(false);
      setZip("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That zip code didn't work.");
    } finally {
      setSavingZip(false);
    }
  };

  const [searching, setSearching] = useState(false);

  const load = () => {
    // ∞ (radius === null) sends an effectively unlimited radius; backend caps at 50mi if omitted.
    const params = { limit: 60, radius_miles: radius == null ? UNLIMITED_RADIUS : radius };
    if (coords) { params.lat = coords.lat; params.lng = coords.lng; }
    api.get("/recipients/nearby", { params })
      .then(({ data }) => setPeople((Array.isArray(data) ? data : data.recipients || data.items || []).map(normRecipient).filter((p) => !p.isMe)))
      .catch(() => {});
  };

  const searchNearby = () => {
    setSearching(true);
    const params = { limit: 60, radius_miles: radius == null ? UNLIMITED_RADIUS : radius };
    if (coords) { params.lat = coords.lat; params.lng = coords.lng; }
    api.get("/recipients/nearby", { params })
      .then(({ data }) => setPeople((Array.isArray(data) ? data : data.recipients || data.items || []).map(normRecipient).filter((p) => !p.isMe)))
      .catch(() => {})
      .finally(() => setTimeout(() => setSearching(false), 700));
  };

  useEffect(() => { if (ready) load(); /* eslint-disable-next-line */ }, [ready, coords, radius]);

  useEffect(() => {
    const fetchFeed = () => {
      const params = { limit: 20, radius_miles: UNLIMITED_RADIUS };
      if (coords) { params.lat = coords.lat; params.lng = coords.lng; }
      api.get("/feed", { params }).then(({ data }) => setFeed((Array.isArray(data) ? data : data.items || data.feed || []).map(normFeed))).catch(() => {});
    };
    fetchFeed();
    api.get("/stats/global")
      .then(({ data }) => setStats({ today: data?.gifts_today ?? data?.today ?? 0, all: data?.gifts_total ?? data?.all_time ?? data?.total ?? 0 }))
      .catch(() => api.get("/stats/public").then(({ data }) => setStats({ today: data?.gifts_today ?? 0, all: data?.gifts_total ?? 0 })).catch(() => {}));
    const t = setInterval(fetchFeed, 8000);
    return () => clearInterval(t);
  }, [coords]);

  const pins = useMemo(() => people.slice(0, 12), [people]);

  // Cycle the bottom bubble through recent donations.
  useEffect(() => {
    if (feed.length <= 1) return undefined;
    const t = setInterval(() => setFeedIdx((i) => (i + 1) % feed.length), 3500);
    return () => clearInterval(t);
  }, [feed.length]);
  const current = feed.length ? feed[feedIdx % feed.length] : null;

  return (
    <div className="relative min-h-screen lg:h-screen overflow-hidden pb-24 lg:pb-0">
      {/* Map surface (always black) with radar rings */}
      <div className="map-surface absolute inset-0">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-pill border border-dashed border-white/10" style={{ width: "70vw", height: "70vw", maxWidth: 620, maxHeight: 620 }} />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-pill border border-dashed border-white/10" style={{ width: "48vw", height: "48vw", maxWidth: 420, maxHeight: 420 }} />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-pill border border-dashed border-white/10" style={{ width: "26vw", height: "26vw", maxWidth: 240, maxHeight: 240 }} />

        {/* pins */}
        {pins.map((p, i) => {
          const slot = pinSlots[i % pinSlots.length];
          const gold = (p.totalReceived || 0) >= 100;
          return (
            <button key={p.id} data-testid={`map-pin-${p.id}`} onClick={() => setSelected(p)}
              className="absolute z-[5] hover:z-20 hover:scale-110 transition-transform animate-pop-in"
              style={{ ...slot, transform: "translate(-50%,-50%)", animationDelay: `${(i % 8) * 0.05}s` }}>
              <CircleAvatar src={p.picture} name={p.name} size={40}
                ring={gold ? "linear-gradient(135deg,#FFD93D,#FF8A3D)" : "#ffffff"}
                glow={gold ? "#FFD93D" : p.hasStory ? "#3B60E8" : undefined} />
            </button>
          );
        })}

        {/* me pin — tap to search for new neighbors */}
        <button data-testid="me-pin" onClick={searchNearby}
          className="absolute z-10 active:scale-95 transition-transform" style={{ left: "50%", top: "50%", transform: "translate(-50%,-50%)" }}>
          <div className="animate-pulse-ring rounded-pill">
            <CircleAvatar src={user?.picture} name={user?.name} size={48} ring="linear-gradient(135deg,#3B60E8,#8B5CF6)" />
          </div>
        </button>

        {/* Searching label — centered on the map, floating above the me pin */}
        {searching && (
          <div className="absolute z-20 left-0 right-0 flex justify-center pointer-events-none" style={{ top: "calc(50% - 63px)" }}>
            <div data-testid="searching-label"
              className="whitespace-nowrap px-3 py-1 rounded-pill text-white text-xs font-black flex items-center gap-1.5 animate-pop-in"
              style={{ background: "#1E40AF" }}>
              <Loader2 size={12} className="animate-spin" /> Searching
            </div>
          </div>
        )}
      </div>

      {/* Top filter bar */}
      <div className="relative z-20 p-3">
        <div className="flex items-center gap-2 bg-surface-2/85 backdrop-blur-md border border-border rounded-pill p-1.5 pl-3 overflow-x-auto slim-scroll">
          <span className="flex items-center gap-1 text-sm font-black text-ink shrink-0"><MapPin size={15} className="text-brand-primary" /> Within</span>
          {RADIUS_OPTIONS.map((r) => (
            <button key={String(r)} data-testid={`radius-${r === null ? "inf" : r}`} onClick={() => setRadius(r)}
              style={{ WebkitTapHighlightColor: "transparent" }}
              className={`shrink-0 px-3 py-1.5 rounded-pill text-sm font-extrabold transition-colors select-none outline-none focus:outline-none focus-visible:outline-none ${radius === r ? "bg-brand-primary text-white" : "text-ink-tertiary bg-transparent"}`}>
              {r === null ? "∞" : `${r} mi`}
            </button>
          ))}
          <button data-testid="map-refresh" onClick={load} style={{ WebkitTapHighlightColor: "transparent" }} className="shrink-0 ml-auto w-9 h-9 rounded-pill grid place-items-center text-ink-tertiary outline-none focus:outline-none">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* About pill — top-left of the map */}
      <button data-testid="about-button" onClick={() => setAboutOpen(true)} aria-label="About Blessed"
        className="absolute z-30 left-3 top-[64px] flex items-center gap-1.5 px-2.5 py-1.5 rounded-pill bg-slate-900/85 backdrop-blur-md shadow-md active:scale-95 transition-transform">
        <Info size={16} strokeWidth={2.6} className="text-white" />
        <span className="text-white font-black text-xs tracking-wide">About</span>
      </button>

      {/* Center CTAs */}
      <div className="relative z-20 flex flex-col items-center gap-2 mt-2 px-6 pointer-events-none">
        <div className="pointer-events-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-pill text-white font-black text-sm shadow-lg" style={{ background: "#1E40AF" }}>
          <Sparkles size={14} className="text-sunny" /> {stats.today} today <span className="opacity-40 font-normal">|</span> {stats.all} all-time
        </div>
        <button data-testid="bless-button" onClick={() => setQuickOpen(true)}
          className="pointer-events-auto w-full max-w-[200px] h-11 rounded-pill font-black text-xl active:scale-95 transition-transform"
          style={{ background: "#FFD93D", color: "#050A1F", boxShadow: "0 0 22px rgba(255,217,61,0.5)" }}>
          Bless!
        </button>
        <button data-testid="bless-multiple-button" onClick={() => setPickerOpen(true)}
          className="pointer-events-auto w-full max-w-[200px] h-10 rounded-pill font-black text-base active:scale-95 transition-transform"
          style={{ background: "#F5A623", color: "#050A1F" }}>
          Bless multiple!
        </button>
        <button data-testid="mini-games-button" onClick={() => navigate("/games")}
          className="pointer-events-auto w-full max-w-[200px] h-10 rounded-pill font-black text-base text-white active:scale-95 transition-transform flex items-center justify-center gap-1.5"
          style={{ background: "#8B5CF6", boxShadow: "0 0 18px rgba(139,92,246,0.4)" }}>
          <span className="text-base">🎡</span> Mini Games
        </button>
        <button data-testid="change-location-button" aria-label="Change location" onClick={() => { setZip(user?.zipcode || ""); setZipOpen(true); }}
          className="pointer-events-auto w-12 h-12 rounded-pill grid place-items-center text-white active:scale-95 transition-transform relative"
          style={{ background: "#3BA7E8", boxShadow: "0 0 20px rgba(59,167,232,0.6)" }}>
          <MapPin size={22} strokeWidth={0} fill="currentColor" />
          <span className="absolute w-[6px] h-[6px] rounded-pill" style={{ background: "#3BA7E8", top: "19px" }} />
        </button>
      </div>

      {/* Change location / zip modal */}
      <Dialog open={zipOpen} onOpenChange={setZipOpen}>        <DialogContent className="bg-surface-2 border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <MapPin size={20} className="text-brand-primary" /> Change your location
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-ink-tertiary font-semibold">
            Enter your US zip code so we can find people near you.
          </p>
          <input
            data-testid="change-zip-input"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
            inputMode="numeric"
            placeholder="00000"
            className="w-full mt-2 py-4 text-center text-3xl font-black tracking-[0.4em] bg-surface border-y-2 border-brand-primary outline-none text-ink"
          />
          <Button data-testid="change-zip-confirm" disabled={savingZip} onClick={saveZip}
            className="w-full h-12 mt-2 rounded-pill font-black text-lg bg-brand-primary text-white">
            {savingZip ? <Loader2 className="animate-spin" size={20} /> : "Update location"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Bottom cycling donations bubble — always visible, fixed above the nav */}
      <button data-testid="map-feed-card" onClick={() => (current ? navigate("/live") : setQuickOpen(true))}
        className="fixed z-30 left-3 right-3 bottom-[calc(env(safe-area-inset-bottom)+70px)] lg:left-[17rem] lg:right-4 lg:bottom-4 flex items-center gap-3 bg-surface-2 border border-border rounded-lg p-3 shadow-2xl text-left">
        <span className={`w-2 h-2 rounded-pill shrink-0 ${current ? "bg-errorb animate-pulse" : "bg-ink-tertiary/50"}`} />
        {current ? (
          <div key={current.id} className="flex items-center gap-3 flex-1 min-w-0 animate-pop-in">
            <div className="flex -space-x-2 shrink-0">
              <CircleAvatar src={current.donorPicture} name={current.donorName} size={30} className="ring-2 ring-surface-2" />
              <CircleAvatar src={current.recipientPicture} name={current.recipientName} size={30} className="ring-2 ring-surface-2" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-sm truncate">{current.donorName} → {current.recipientName}</div>
              <div className="text-xs text-ink-tertiary">{clockTime(current.createdAt)} · tap to open</div>
            </div>
            <span className="shrink-0 font-black text-white text-sm px-2.5 py-1 rounded-pill" style={{ background: "#1E40AF" }}>{money(current.amount)}</span>
          </div>
        ) : (
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-[30px] h-[30px] rounded-pill grid place-items-center shrink-0 text-white" style={{ background: "#1E40AF" }}>
              <Sparkles size={16} className="text-sunny" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-sm truncate">No blessings yet nearby</div>
              <div className="text-xs text-ink-tertiary">Be the first — tap to bless someone</div>
            </div>
          </div>
        )}
        <Heart size={18} className="shrink-0 text-errorb" fill="currentColor" />
      </button>

      {/* Recipient sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="bottom" className="bg-surface-2 border-border rounded-t-lg max-h-[80vh]">
          {selected && (
            <div className="animate-slide-up">
              <SheetHeader>
                <div className="flex items-center gap-4">
                  <CircleAvatar src={selected.picture} name={selected.name} size={68} ring="linear-gradient(135deg,#1E40AF,#8B5CF6)" />
                  <div className="text-left">
                    <SheetTitle className="text-xl font-black">{selected.name}</SheetTitle>
                    <div className="text-sm text-ink-tertiary font-semibold">
                      {selected.distance != null && <span>{selected.distance.toFixed(1)} mi away</span>}
                      {selected.totalReceived > 0 && <span> · {money(selected.totalReceived)} received</span>}
                    </div>
                  </div>
                </div>
              </SheetHeader>
              {selected.story && <p className="text-sm text-ink-tertiary leading-relaxed mt-4">{selected.story}</p>}
              <Button data-testid="sheet-donate-button" onClick={() => navigate(`/donate/${selected.id}`)}
                className="w-full h-12 mt-6 rounded-pill font-extrabold bg-brand-primary hover:opacity-90 text-white">
                <HandHeart size={18} className="mr-1" /> Bless {selected.name.split(" ")[0]}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <BlessPicker open={pickerOpen} onOpenChange={setPickerOpen} coords={coords} />
      <QuickBless open={quickOpen} onOpenChange={setQuickOpen} people={people} />

      {/* About Blessed modal — static, backdrop click does NOT close (only the X) */}
      {aboutOpen && (
        <div data-testid="about-overlay" className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[88vh] overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2 className="text-lg font-black text-slate-900">About Blessed</h2>
              <button data-testid="about-close" onClick={() => setAboutOpen(false)} aria-label="Close About"
                className="w-8 h-8 rounded-pill bg-slate-200 flex items-center justify-center hover:bg-slate-300 transition-colors">
                <X size={20} strokeWidth={2.6} className="text-slate-900" />
              </button>
            </div>
            <div className="overflow-y-auto p-5 space-y-4" style={{ maxHeight: "calc(88vh - 57px)" }}>
              <h1 className="text-[22px] font-black text-brand-primary leading-tight">Blessed — Kindness on the map.</h1>
              <p className="text-slate-900 text-[15px] leading-relaxed">
                Blessed is a donation app that turns generosity into something you can see, feel, and be part of. Open it up and you'll find a live map of real people near you — neighbors who could use a hand — each just one tap away from a little kindness.
              </p>
              <h3 className="text-base font-black text-slate-900 mt-1">How it works:</h3>
              <p className="text-slate-900 text-[15px] leading-relaxed">🗺️ <span className="font-black">See who's near you.</span> Real people appear as pins on your map, from a few blocks away to as far as you choose.</p>
              <p className="text-slate-900 text-[15px] leading-relaxed">💛 <span className="font-black">Bless someone in one tap.</span> Send $5 or more securely through Stripe, Zelle, or Venmo. Big or small, every blessing lands directly with the person who needs it.</p>
              <p className="text-slate-900 text-[15px] leading-relaxed">⚡ <span className="font-black">Watch kindness happen live.</span> A real-time feed shows blessings flowing between neighbors as they happen — cheer them on, heart them, and say thanks.</p>
              <p className="text-slate-900 text-[15px] leading-relaxed">🎡 <span className="font-black">Give through play.</span> Spin the Wheel, Pac-Man, and other mini-games pick a lucky neighbor for you to bless — generosity has never been this fun.</p>
              <p className="text-slate-900 text-[15px] leading-relaxed">🙌 <span className="font-black">Need a hand yourself?</span> Share your story to become a recipient, connect your bank, and receive blessings from your community.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
